import { AxiosError } from 'axios'

interface IOracleErrorDetail {
  'o:errorPath'?: string
  'o:errorCode'?: string
}

interface IOracleErrorBody {
  'o:errorDetails'?: IOracleErrorDetail[]
}

interface IDependencyProblemDetails {
  // Sanitised Oracle reason the BFF sends in every environment.
  dependencyReason?: unknown
  // Oracle's error code (e.g. XXCL_CREAT_PAT_DUP_PAT_ERR) the BFF sends in every environment.
  dependencyErrorCode?: unknown
  dependency?: {
    // Raw Oracle body, only sent when ShowDebugInfo is on. The BFF sends it as
    // a string; GOS (PR 7678) sends it as a JSON object.
    response?: unknown
  }
}

const readProblemDetails = (
  error: AxiosError
): IDependencyProblemDetails | undefined =>
  error?.response?.data as IDependencyProblemDetails | undefined

/**
 * Oracle's raw error body as text, whichever shape the BFF or GOS returned it
 * in. Use this wherever legacy code runs string parsing over the response.
 */
export const getDependencyResponseText = (
  error: AxiosError
): string | undefined => {
  const response = readProblemDetails(error)?.dependency?.response

  if (typeof response === 'string') {
    return response
  }

  if (response && typeof response === 'object') {
    try {
      return JSON.stringify(response)
    } catch {
      return undefined
    }
  }

  return undefined
}

const parseOracleBody = (error: AxiosError): IOracleErrorBody | undefined => {
  const response = readProblemDetails(error)?.dependency?.response

  if (response && typeof response === 'object') {
    return response as IOracleErrorBody
  }

  if (typeof response !== 'string') {
    return undefined
  }

  try {
    const parsed = JSON.parse(response)
    return parsed && typeof parsed === 'object' ? parsed : undefined
  } catch {
    return undefined
  }
}

const CDATA_SECTION = /<!\[CDATA\[([\s\S]*?)\]\]>/g

/**
 * OIC faults put an XML fault document into o:errorPath. Keep only its first
 * human-readable CDATA line, and drop it when it is a raw transport dump that
 * would leak internal URLs to the user.
 */
const toReadableErrorPath = (errorPath: unknown): string | undefined => {
  if (typeof errorPath !== 'string') {
    return undefined
  }

  const trimmed = errorPath.trim()
  if (!trimmed) {
    return undefined
  }

  if (!trimmed.includes('<')) {
    return trimmed
  }

  const firstCdata = Array.from(trimmed.matchAll(CDATA_SECTION))
    .map((match) => match[1].trim())
    .find((text) => text.length > 0)

  if (!firstCdata || firstCdata.includes('://')) {
    return undefined
  }

  return firstCdata
}

/**
 * The user-facing reason Oracle gave for a failed dependency call, or
 * undefined when there is none. Prefers the BFF's sanitised dependencyReason,
 * then falls back to o:errorDetails[].o:errorPath in the raw body.
 */
export const getDependencyErrorMessage = (
  error: AxiosError
): string | undefined => {
  const reason = readProblemDetails(error)?.dependencyReason
  if (typeof reason === 'string' && reason.trim().length > 0) {
    return reason.trim()
  }

  const details = parseOracleBody(error)?.['o:errorDetails']
  if (!Array.isArray(details)) {
    return undefined
  }

  return details
    .map((detail) => toReadableErrorPath(detail?.['o:errorPath']))
    .find((text): text is string => !!text)
}

// Oracle codes such as XXCL_CREAT_PAT_DUP_PAT_ERR; Oracle also repeats the HTTP
// status ("400") in o:errorCode, which is not a code to branch on.
const ORACLE_ERROR_CODE = /^[A-Z][A-Z0-9_]{2,99}$/

/**
 * Oracle's error code for a failed dependency call, or undefined. Prefers the
 * BFF's dependencyErrorCode (every environment), then the raw body's
 * o:errorDetails[].o:errorCode (environments with ShowDebugInfo on).
 */
export const getDependencyErrorCode = (
  error: AxiosError
): string | undefined => {
  const code = readProblemDetails(error)?.dependencyErrorCode
  if (typeof code === 'string' && ORACLE_ERROR_CODE.test(code.trim())) {
    return code.trim()
  }

  const details = parseOracleBody(error)?.['o:errorDetails']
  if (!Array.isArray(details)) {
    return undefined
  }

  return details
    .map((detail) => detail?.['o:errorCode'])
    .find(
      (value): value is string =>
        typeof value === 'string' && ORACLE_ERROR_CODE.test(value)
    )
}

const DUPLICATE_PATIENT_ERROR_CODE = 'XXCL_CREAT_PAT_DUP_PAT_ERR'
const DUPLICATE_PATIENT_NUMBER = /Patient Number:\s*([^\s,]+)/i

/**
 * The existing patient's number when Oracle rejected createPatient as a
 * duplicate ("Patient already exists ... with Patient Number: CAMP-OU3194 and
 * ..."), else undefined. Works without ShowDebugInfo.
 */
export const getDuplicatePatientNumber = (
  error: AxiosError
): string | undefined => {
  if (getDependencyErrorCode(error) !== DUPLICATE_PATIENT_ERROR_CODE) {
    return undefined
  }

  return getDependencyErrorMessage(error)?.match(DUPLICATE_PATIENT_NUMBER)?.[1]
}

/**
 * Oracle's text for the duplicate-PO parsers, which need it even when the
 * BFF's dependency.response is GOS's ProblemDetails with Oracle's body nested
 * inside (as an object since GOS PR 7678). Always returns a string, falling
 * back to Oracle's sanitised reason (environments without ShowDebugInfo), then
 * the Axios message, so callers can run .match() safely.
 */
export const getInnermostDependencyResponseText = (
  error: AxiosError
): string => {
  const text = getDependencyResponseText(error)
  if (!text) {
    return getDependencyErrorMessage(error) ?? error?.message ?? ''
  }

  try {
    const inner = JSON.parse(text)?.dependency?.response
    if (typeof inner === 'string') {
      return inner
    }
    if (inner && typeof inner === 'object') {
      return JSON.stringify(inner)
    }
  } catch {
    // Not JSON: the text already is Oracle's message.
  }

  return text
}
